/**
 * Fast Simon — results controller for both Fast Simon listings (MPA / Shopify Liquid).
 *
 * Fast Simon owns WHICH products appear and in WHAT order; Shopify Liquid still renders the
 * cards. For every page we ask the Section Rendering API for `sections/fs-search-cards.liquid`,
 * which renders the theme's native `_product-card` block for the handles Fast Simon returned.
 * Both surfaces are therefore the same markup — and the same design — as the native grid, and
 * they follow any change made to the card in the theme editor without touching this file.
 *
 * Two surfaces, chosen by which custom element the template renders:
 *
 *   <fs-search-config>     /search          window.FastSimonSDK.fullTextSearch()
 *   <fs-collection-config> /collections/…   window.FastSimonSDK.smartCollections()
 *
 * Facets, the price slider, chips, sort, promo tiles, numbered pagination, viewport impressions, swatch
 * attribution and card fetching are identical on both, which is why this is one file and not
 * two. Only three things differ — the SDK method, the listing-view event and the click events —
 * and they are collected in SURFACE below.
 *
 * Loaded on the search and collection templates (see layout/theme.liquid).
 *
 * Performance notes (per the repo's performance rules):
 *  - no scroll listeners, no intervals, no document-wide DOM scans;
 *  - card chunks are fetched in parallel and every in-flight request is abortable, so a fast
 *    filter/sort sequence never renders a stale response;
 *  - the "search within results" input is debounced at 250ms;
 *  - `withAttributes: false` is passed explicitly — the store's global SDK config sets it to
 *    true, which costs latency we do not need here because the cards come from Liquid.
 *
 * Idempotency: Fast Simon callbacks re-fire on every lifecycle update. Every DOM mutation
 * below either replaces a container wholesale or is guarded by a dataset flag.
 */
(() => {
  'use strict';

  if (window.__fsResultsControllerLoaded) return;

  /**
   * Controller parse time. The takeover cost is measured as fs-results:commit −
   * fs-results:run (request start → grid committed); this mark anchors the script's own
   * execution in the same trace.
   */
  performance.mark('fs-results:boot');

  /** Shopify renders at most 20 `all_products` lookups per template. */
  const CARD_CHUNK_SIZE = 20;
  /** First token of `q` that tells fs-search-cards.liquid this is a card request. */
  const CARD_TOKEN = 'fscardsrender';
  /**
   * Price facet keys. They are never rendered as checkbox groups: this store has no price
   * buckets (`Price` comes back with an empty value list), and `Price_min` / `Price_max` are
   * bare numeric bounds (`[124]`, `[1748]`), not `[value, count]` rows. They feed the price
   * range slider instead — see buildPriceGroup().
   */
  const SKIPPED_FACETS = new Set(['Price', 'Price_min', 'Price_max']);

  /**
   * The narrowBy key Fast Simon accepts for a price range; the value is `"<from>-<to>"` with
   * no spaces. Verified live on this store ("gown": 1086 results -> 93 for `200-400`).
   * `Price_min` / `Price_max` and `Price` are response-only keys — sending them filters
   * nothing.
   */
  const PRICE_KEY = 'Price_from_to';

  /**
   * Some facets are configured on the raw metaobject reference field (`Shopify.neckline`,
   * `Shopify.dress-style`) rather than its `.label` companion. Fast Simon resolves those
   * references to display names from its own index, and any metaobject created since the
   * last full sync has no entry — so the value comes back as a literal
   * `gid://shopify/Metaobject/...` and would render as that string in the filter list.
   *
   * Hiding them is a stopgap: the values stay filterable by URL, they just are not offered.
   * The real fix is in the Fast Simon dashboard — point those facets at the `.label`
   * attribute (as the Body Shape facet already is, which never leaks a gid) or re-sync so
   * the new metaobjects are indexed.
   */
  const UNRESOLVED_VALUE = /^gid:\/\//;

  /**
   * Which facet renders as colour swatches.
   *
   * Matched on the key OR the dashboard label rather than an exact key, because the key is a
   * dashboard-side choice that changes: this facet has already been `Color` (the raw product
   * option) and is now `Isp-color-family` (Fast Simon's colour family), and an exact-match
   * list silently dropped it back to text rows on the rename.
   */
  const SWATCH_FACET = /colou?r/i;
  const isSwatchFacet = (key, label) => SWATCH_FACET.test(key || '') || SWATCH_FACET.test(label || '');

  /**
   * Colour-name → CSS colour for the swatch facet.
   *
   * Fast Simon's Color facet returns the raw product option value ("Black/Nude", "Mystic
   * Blue", "Bamboo") and carries no colour data, whereas the collection page's swatches come
   * from Shopify's colour-pattern taxonomy, which ships its own hex per value. The two are
   * different vocabularies, so the swatch colour has to be resolved here.
   *
   * The facet now returns Fast Simon's colour FAMILY (Blue, Black, Beige…) rather than the
   * raw option value, so most entries are single words and resolve exactly.
   *
   * Group 1 are the exact rgb values this theme renders for the Shopify colour-pattern
   * taxonomy, sampled from the collection filter — a family matching one of these is
   * pixel-identical to the collection page. Group 2 covers the remaining families, and
   * group 3 is legacy raw-option vocabulary kept so the facet still renders if the dashboard
   * is ever pointed back at the product option.
   *
   * Anything unresolved renders as `swatch--unavailable`, the same treatment the theme gives
   * "Multicolor".
   */
  const SWATCH_COLOURS = {
    // 1 — sampled from this theme's Shopify taxonomy swatches
    black: 'rgb(0 0 0)',
    blue: 'rgb(0 91 211)',
    gold: 'rgb(212 154 6)',
    gray: 'rgb(128 128 128)',
    grey: 'rgb(128 128 128)',
    green: 'rgb(5 170 61)',
    navy: 'rgb(40 32 153)',
    pink: 'rgb(255 192 203)',
    purple: 'rgb(165 77 207)',
    red: 'rgb(246 31 31)',
    white: 'rgb(255 255 255)',
    // 2 — remaining colour families (not sampled; confirm with design)
    beige: 'rgb(226 202 173)',
    brown: 'rgb(124 82 56)',
    orange: 'rgb(255 122 28)',
    yellow: 'rgb(255 210 32)',
    silver: 'rgb(192 192 192)',
    cream: 'rgb(248 240 222)',
    tan: 'rgb(198 166 128)',
    taupe: 'rgb(164 149 135)',
    teal: 'rgb(0 128 128)',
    turquoise: 'rgb(48 190 190)',
    burgundy: 'rgb(114 26 44)',
    maroon: 'rgb(110 31 44)',
    olive: 'rgb(112 112 56)',
    khaki: 'rgb(172 158 116)',
    bronze: 'rgb(150 100 52)',
    copper: 'rgb(184 106 62)',
    rose: 'rgb(226 146 150)',
    peach: 'rgb(255 190 152)',
    lavender: 'rgb(186 164 214)',
    charcoal: 'rgb(54 58 61)',
    // 3 — legacy raw-option vocabulary
    ivory: 'rgb(255 255 240)',
    petal: 'rgb(250 228 226)',
    nude: 'rgb(226 202 184)',
    midnight: 'rgb(25 25 60)',
    emerald: 'rgb(4 135 94)',
    pine: 'rgb(30 74 58)',
    cerulean: 'rgb(0 123 187)',
    bamboo: 'rgb(216 192 120)',
    lava: 'rgb(207 42 26)',
    mystic: 'rgb(102 134 173)',
    antique: 'rgb(214 165 165)',
    pale: 'rgb(250 218 221)',
    // unresolved on purpose — rendered as swatch--unavailable
    floral: '',
    metallic: '',
    multicolor: '',
    multicolour: '',
  };

  /**
   * Two different conventions in this catalogue, so the order matters:
   *   "Black/Nude", "Ivory/Petal"  — slash separates two colours, the first one leads
   *   "Pale Pink", "Mystic Blue"   — a modifier before the colour, so the last word leads
   * Hence: take the segment before the first slash, then its last word.
   *
   * @param {string} value a facet value such as "Navy/Nude" or "Pale Pink"
   * @returns {string} a CSS colour, or '' when unresolved
   */
  const swatchColour = (value) => {
    const primary = String(value).toLowerCase().split('/')[0];
    const words = primary.split(/[\s,&-]+/).filter(Boolean);
    for (const word of [words[words.length - 1], words[0]]) {
      if (word && Object.prototype.hasOwnProperty.call(SWATCH_COLOURS, word)) {
        return SWATCH_COLOURS[word];
      }
    }
    return '';
  };

  /** The theme's checkmark, cloned from the template the section renders. */
  let checkmarkMarkup = null;
  const checkmarkSvg = () => {
    if (checkmarkMarkup === null) {
      const tpl = document.getElementById('fs-facet-checkmark');
      checkmarkMarkup = tpl ? tpl.innerHTML.trim() : '';
    }
    return checkmarkMarkup;
  };
  const DEBOUNCE_MS = 250;

  /**
   * Label for the "no explicit sort" row in the sort UI. Deliberately NOT a SortBy key: the
   * controller cannot know which SortBy the Fast Simon dashboard applies by default, so the
   * untouched state is labelled honestly — and the row is how a shopper RETURNS to that
   * default once an explicit sort has been picked.
   */
  const DEFAULT_SORT_LABEL = 'Default';

  const SORT_LABELS = {
    relevency: 'Relevance',
    popularity: 'Best selling',
    price_min_to_max: 'Price, low to high',
    price_max_to_min: 'Price, high to low',
    creation_date: 'Newest',
    creation_date_oldest: 'Oldest',
    reviews: 'Highest rated',
    a_to_z: 'A–Z',
    z_to_a: 'Z–A',
    discount_max_to_min: 'Discount, high to low',
    discount_min_to_max: 'Discount, low to high',
  };


  /**
   * Shopify's `?sort_by=` values mapped to the Fast Simon equivalent. `manual` — the collection's
   * own curated order — has no Fast Simon counterpart; dropping it lets the dashboard's configured
   * default apply, which is the closest thing to "the merchant's order" that exists here.
   */
  const SHOPIFY_SORT_MAP = {
    manual: null,
    featured: null,
    'best-selling': 'popularity',
    'alpha-ascending': 'a_to_z',
    'alpha-descending': 'z_to_a',
    'price-ascending': 'price_min_to_max',
    'price-descending': 'price_max_to_min',
    'created-ascending': 'creation_date_oldest',
    'created-descending': 'creation_date',
  };

  /* ------------------------------------------------------------------ config */

  // Every setting below is (re)derived by readConfig() — at boot, and again by
  // reinitialize() after a theme-editor section re-render, whose fresh config element can
  // carry different settings (products per page, sort options, labels, …).
  let configEl = null;
  let grid = null;
  /** True on a collection PLP (`<fs-collection-config>`); false on /search. */
  let isCollection = false;
  /**
   * The only three places the surfaces differ. Event names are preference lists because fsEvent()
   * resolves them against the enum the loaded SDK build actually exposes, and this store's build
   * has lagged the documented names before. `viewEventWithin` is used when the shopper narrowed the
   * listing with the search-within box: the docs fire `SearchWithinPerformed` for that case on both
   * surfaces (the collection guide's example switches on `searchWithinSearch` too), so it is listed
   * first and fsEvent falls back to the listing event — whose payload carries the term in `query` —
   * when the loaded build lacks it.
   * @type {{method: string, viewEvent: string|string[], viewEventWithin: string[], productClickEvent: string, promoTileClickEvent: string, quickViewEvent: string[]}|null}
   */
  let SURFACE = null;
  let gridSectionId = '';
  let searchUrl = '/search';
  let perPage = 24;
  let sortingEnabled = false;
  let filteringEnabled = false;
  /** Only documented SortBy keys reach the SDK: SORT_LABELS covers the full SortBy union. */
  let sortKeys = ['relevency'];
  let emptyLabel = 'No products found';
  /** The Fast Simon category id of the collection being viewed — `collection.id` in Liquid. */
  let categoryID = '';

  /**
   * (Re)derives every setting from the CURRENT config element and grid. Returns false when
   * the page no longer renders a usable Fast Simon surface (no config element or grid, or a
   * collection without a category id) — the caller must not run the takeover then.
   *
   * @returns {boolean}
   */
  const readConfig = () => {
    configEl = document.querySelector('fs-search-config, fs-collection-config');
    grid = document.querySelector('ul.product-grid');
    // The bare window.FastSimonSDK symbol is deliberately NOT required here: this script ships
    // in the initial HTML and may execute before the SDK. Everything that needs the SDK waits
    // behind the readiness gate below instead — never start from the bare symbol.
    if (!configEl || !grid) return false;

    isCollection = configEl.tagName.toLowerCase() === 'fs-collection-config';
    SURFACE = isCollection
      ? {
          method: 'smartCollections',
          viewEvent: ['SmartCollectionPreformed', 'SmartCollectionPerformed'],
          viewEventWithin: ['SearchWithinPerformed', 'SmartCollectionPreformed', 'SmartCollectionPerformed'],
          productClickEvent: 'CollectionProductClicked',
          promoTileClickEvent: 'CollectionPromoTileClicked',
          quickViewEvent: ['QuickViewClickedCollection', 'quickViewCollection'],
        }
      : {
          method: 'fullTextSearch',
          viewEvent: 'SearchPerformed',
          viewEventWithin: ['SearchWithinPerformed', 'SearchPerformed'],
          productClickEvent: 'SerpProductClicked',
          promoTileClickEvent: 'SerpPromoTileClicked',
          quickViewEvent: ['QuickViewClickedSerp', 'quickViewSerp'],
        };

    gridSectionId = configEl.dataset.gridSectionId || '';
    searchUrl = configEl.dataset.searchUrl || '/search';
    perPage = parseInt(configEl.dataset.productsPerPage, 10) || (isCollection ? 24 : 40);
    sortingEnabled = configEl.dataset.enableSorting === 'true';
    filteringEnabled = configEl.dataset.enableFiltering === 'true';
    // A typo in the section's "Sort options" text is dropped instead of being sent as an
    // invalid sortBy. An empty result (blank or all-invalid setting) falls back to the
    // dashboard default.
    sortKeys = (configEl.dataset.sortOptions || 'relevency')
      .split(',')
      .map((s) => s.trim())
      .filter((key) => key && Object.prototype.hasOwnProperty.call(SORT_LABELS, key));
    if (!sortKeys.length) sortKeys.push('relevency');
    emptyLabel = (configEl.dataset.labels || 'No products found').split('|')[0];
    categoryID = configEl.dataset.categoryId || '';
    return !(isCollection && !categoryID); // a collection without a category has nothing to ask for
  };

  if (!readConfig()) return;
  window.__fsResultsControllerLoaded = true;

  /* ---------------------------------------------------------------- readiness */

  /**
   * The SDK is only guaranteed to behave from its onReady (initialization finished) onward —
   * calls made in that gap can be dropped without firing their callback (the same contract
   * fs-sdk-cart.js and fs-sdk-recommendations.js follow). Because this script now ships in
   * the initial HTML (defer) instead of being injected from onReady, it can execute BEFORE
   * the SDK: the first run(), later filter/sort/pagination runs, and event sends all wait
   * behind this gate. Readiness sources, in order: the flag already set (this script ran
   * late), a chained wrap of the shared config's onReady, and a bounded poll as last resort.
   */
  const sdkUsable = () =>
    window.__fastSimonSdkReady === true &&
    typeof window.FastSimonSDK === 'object' &&
    typeof window.FastSimonSDK[SURFACE.method] === 'function';
  // Initialized from the combined check, not the flag alone (the same contract as
  // fs-sdk-cart.js): a flag that somehow lacks a usable surface must still arm the hook
  // and poll below instead of pretending readiness.
  let sdkReady = sdkUsable();
  /**
   * The run() requested before readiness. Rapid taps during the SDK-init gap collapse into
   * the last one (each run() supersedes its predecessor anyway), so readiness starts exactly
   * one request instead of replaying a queue of doomed-to-abort ones.
   * @type {{page?: number}|null}
   */
  let pendingRun = null;

  const onSdkReady = () => {
    if (sdkReady || !sdkUsable()) return;
    sdkReady = true;
    window.__fastSimonSdkReady = true;
    if (pendingRun) {
      const options = pendingRun;
      pendingRun = null;
      runNow(options);
    }
  };

  if (!sdkReady) {
    const config = window.__fast_simon_sdk_config;
    if (config && typeof config.onReady === 'function' && !config.__fsResultsHooked) {
      config.__fsResultsHooked = true;
      const onReady = config.onReady;
      config.onReady = function (...args) {
        try {
          if (typeof onReady === 'function') onReady.apply(this, args);
        } finally {
          window.__fastSimonSdkReady = true;
          // Act only if the SDK is genuinely usable; otherwise leave it to the poll, which
          // gates on the same combined condition and cannot be stranded by this flag.
          onSdkReady();
        }
      };
    }

    let pollTries = 0;
    const poll = () => {
      if (sdkUsable()) return onSdkReady();
      if (pollTries >= 16) {
        // The chained onReady above stays armed, so a LATE init still takes over; only the
        // safety-net poll stops. What this bounds is a flag that never gets an onReady behind
        // it — warn so the stranded state (native grid, inert FS chrome) is visible.
        console.warn('[Fast Simon] SDK not ready in time; results takeover still waiting on onReady');
        return;
      }
      pollTries += 1;
      window.setTimeout(poll, 500);
    };
    poll();
  }

  /**
   * The cards section's generated id, or '' when it is not known yet.
   *
   * On /search the section is on the same page, so the marker it renders gives the id directly.
   * On a collection page the cards live on the *search* template, whose generated id carries that
   * template's uuid prefix — unknowable from collection Liquid, and hardcoding it would break the
   * moment the template is recreated in the theme editor. So the id is harvested from the first
   * whole-page card response and cached for the rest of the session; until then fetchCards()
   * requests the full /search page and reads the `<li>` elements out of that. Same cards, more
   * bytes, and only on the first listing render of a session.
   */
  const CARDS_ID_KEY = 'fs-cards-section-id';
  const safeStorage = (run) => {
    try {
      return run();
    } catch (error) {
      return ''; // throws in private mode; a whole-page response works without it
    }
  };
  let cardsSectionId =
    (() => {
      const marker = document.querySelector('[data-fs-cards-section]');
      const wrapper = marker && marker.closest('.shopify-section');
      if (wrapper && wrapper.id) return wrapper.id.replace(/^shopify-section-/, '');
      return configEl.dataset.cardsSectionId || '';
    })() || safeStorage(() => window.sessionStorage.getItem(CARDS_ID_KEY));

  /** Caches the id learned from a whole-page response so later chunks get a cheap fragment. */
  const rememberCardsSectionId = (doc) => {
    if (cardsSectionId) return;
    const marker = doc.querySelector('[data-fs-cards-section]');
    const wrapper = marker && marker.closest('.shopify-section');
    if (!wrapper || !wrapper.id) return;
    cardsSectionId = wrapper.id.replace(/^shopify-section-/, '');
    safeStorage(() => window.sessionStorage.setItem(CARDS_ID_KEY, cardsSectionId));
  };

  /**
   * The facet chrome exists twice — the desktop bar (#filter-bar) and the mobile drawer
   * (#filters-drawer), exactly as the native filters block does it. The theme's own CSS
   * shows one and hides the other at the 750px breakpoint, so every render below writes to
   * both copies. They are NOT under a common root, hence document-level queries.
   */
  const $$ = (selector) => [...document.querySelectorAll(selector)];
  // `let` because a theme-editor section re-render REPLACES the nodes these point at: the
  // reinitialize() below re-acquires every one of them, and a controller still holding the
  // detached originals would render the takeover into dead DOM.
  let groupContainers = $$('[data-fs-facet-groups]');
  let chipContainers = $$('[data-fs-chips]');
  let countEls = $$('[data-fs-count]');
  let sortWrappers = $$('[data-fs-sort]');
  let sortOptionContainers = $$('[data-fs-sort-options]');
  let sortCurrentEls = $$('[data-fs-sort-current]');
  let filterCountEls = $$('[data-fs-filter-count]');
  let seeResultsEls = $$('[data-fs-see-results]');
  let gridWrapper = grid.parentElement;

  /* ------------------------------------------------------------------- state */

  const state = {
    term: '',
    page: 1,
    sortBy: null, // only sent when the shopper picks one — otherwise the dashboard default wins
    narrowBy: [], // [[group, value], ...]
    searchWithin: '',
    totalResults: 0,
    pageCount: 1,
  };

  let cardsAbort = null;
  let requestSeq = 0;
  let requestTimer = null;
  const REQUEST_TIMEOUT_MS = 30000;
  let debounceTimer = null;
  let priceCommitTimer = null;
  /** True while a slider thumb is held, so a late response cannot rebuild it mid-drag. */
  let priceDragging = false;
  /** Facets that arrived during a drag; rendered on release. */
  let pendingFacets = null;
  /** Last bounds Fast Simon sent, kept for the zero-result case (see renderFacets). */
  let lastPriceBounds = null;

  /* --------------------------------------------------------------- utilities */

  const esc = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );

  /**
   * A URL fit for an href or a navigation: absolute http(s) or relative — anything with a
   * different scheme (javascript:, data:, vbscript:, …) is rejected. The scheme test runs on
   * the form browsers normalize to (tab/CR/LF stripped, leading/trailing controls trimmed),
   * so `java\nscript:` cannot slip through, and `esc()` at the call site already prevents
   * attribute breakout / entity tricks — this closes the remaining gap: the scheme itself.
   * Both Fast Simon-supplied link sources (promo tiles, turbolink redirects) pass through here.
   *
   * @param {string} url
   * @returns {string} the original URL when safe, '' when rejected
   */
  const safeUrl = (url) => {
    const value = String(url == null ? '' : url);
    const normalized = value
      .replace(/[\t\n\r]/g, '')
      .replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '');
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(normalized);
    if (scheme && !/^https?$/i.test(scheme[1])) return '';
    return value;
  };

  // Elements that can execute script, load active content, or hijack the page. `template` and
  // `math` are dropped as well: template content is inert only until instantiated, and the
  // MathML namespace has a history of parser-confusion (mXSS) bypasses in hand-rolled walkers.
  const PROMO_DROP_TAGS = new Set([
    'script', 'iframe', 'object', 'embed', 'base', 'link', 'meta', 'form', 'template', 'math',
  ]);
  /** Attributes fetched or visited as URLs — routed through safeUrl, dropped when rejected. */
  const PROMO_URL_ATTRS = new Set(['href', 'src', 'poster', 'action', 'formaction', 'xlink:href']);

  /**
   * Best-effort hardening for merchant-authored promo HTML (the dashboard's raw-HTML field)
   * before it touches the page — NOT a security boundary. The author of this markup already
   * holds equivalent power through the theme editor, so this guard exists to catch accidents
   * and compromised-dashboard content, not a hostile author; truly untrusted markup would
   * require a maintained sanitizer (e.g. DOMPurify), which this theme does not ship.
   * What it does enforce: no script-capable elements (incl. template/MathML — see the drop
   * set), no `on*` handlers, no URL attribute escaping the safeUrl scheme check (SVG hrefs
   * included). Inline CSS is left alone — no script execution path in modern browsers — and
   * image-context URLs are inert by definition.
   *
   * @param {string} html
   * @returns {string} sanitized markup
   */
  const sanitizePromoHtml = (html) => {
    const parsed = new DOMParser().parseFromString(String(html == null ? '' : html), 'text/html');
    for (const el of [...parsed.body.querySelectorAll('*')]) {
      if (PROMO_DROP_TAGS.has(el.tagName.toLowerCase())) {
        el.remove(); // children leave with it; their later array entries are harmlessly detached
        continue;
      }
      for (const attr of [...el.attributes]) {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on')) el.removeAttribute(attr.name);
        else if (PROMO_URL_ATTRS.has(name) && !safeUrl(attr.value)) el.removeAttribute(attr.name);
      }
    }
    return parsed.body.innerHTML;
  };

  /** `Color` -> `color`, `Product.body shape.label` -> `product-body-shape-label` */
  const slug = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  /**
   * Short deterministic hash of the RAW label, appended to slug() wherever an element id is
   * built. slug() collapses distinct labels onto one id ("A/B" and "A B" both → `a-b`), which
   * would make every `<label for>` point at the first duplicate and break its checkbox. The
   * hash restores uniqueness while staying stable across re-renders (same input → same id),
   * which is what open-panel and focus restoration rely on.
   *
   * @param {string} value
   * @returns {string} base-36 hash
   */
  const idHash = (value) => {
    const text = String(value == null ? '' : value);
    let hash = 5381;
    for (let i = 0; i < text.length; i += 1) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    return (hash >>> 0).toString(36);
  };

  const handleFromUrl = (url) => {
    const match = /\/products\/([^/?#]+)/.exec(url || '');
    return match ? match[1] : null;
  };

  const chunk = (items, size) => {
    const out = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
  };

  /**
   * Attribution for the grid the shopper is CURRENTLY looking at, captured the moment a render
   * commits its cards to the DOM. `state` mutates the instant a filter, sort or search-within
   * changes, but the previous cards stay on screen until the new response finishes rendering — a
   * second round trip (Section Rendering API) that can take seconds — and stay there if that
   * request then fails. A click in that window belongs to the listing that produced the visible
   * cards, not the one still loading, so the click handlers read this snapshot instead of live
   * `state`. Seeded at startup for the server-rendered cards, and re-captured by renderProducts
   * every time it commits a grid.
   * @type {{term: string, searchWithin: string}}
   */
  let renderedAttribution = { term: state.term, searchWithin: state.searchWithin };

  /** Re-captures the attribution snapshot from the current state (called when a render commits). */
  const commitAttribution = () => {
    renderedAttribution = { term: state.term, searchWithin: state.searchWithin };
  };

  /**
   * The key that ties a click to the listing it came from. The two surfaces spell the id
   * differently — `query` on /search, `collectionID` on a collection (the listing-view event for
   * collections spells the same value `categoryID`) — and a click reported without it is filed
   * against nothing, because neither event can infer which listing the shopper was looking at.
   * Read from the rendered snapshot (see renderedAttribution), never live state.
   */
  const attribution = () =>
    isCollection
      ? {
          collectionID: categoryID,
          query:
            renderedAttribution.searchWithin ||
            window.__fastSimonPageContext?.collectionTitle ||
            undefined,
        }
      : { query: renderedAttribution.term };

  /**
   * Re-runs inline <script> tags that arrived inside injected cards.
   *
   * Cards are parsed with DOMParser, and a parser-created script is flagged "already started"
   * — moving it into the document never executes it. The theme relies on one such script:
   * the `[data-product-map-hover-swatch]` handler in snippets/variant-swatches.liquid that
   * swaps the card's primary image when a colour swatch is hovered. Without this the search
   * grid silently loses that behaviour while collections keep it.
   *
   * Only executable scripts are re-created — the per-card `application/json` variant blobs
   * are data and must stay exactly where they are, and promo-tile scripts stay inert (guard
   * below). Deduplication is by body and scoped to renders THIS controller fetched: a body
   * that already ran on the initial server-rendered page runs once more on the first
   * takeover. That is accepted deliberately — a per-node script needs that re-run (its old
   * nodes are gone), and for the delegation-style script this theme actually ships the extra
   * listener is idempotent. After the first takeover, re-renders never stack duplicates.
   *
   * @param {ParentNode} root
   */
  const EXECUTABLE_SCRIPT_TYPES = new Set(['', 'text/javascript', 'application/javascript', 'module']);
  const executedScripts = new Set();

  const runInjectedScripts = (root) => {
    for (const original of root.querySelectorAll('script')) {
      // Promo tiles carry merchant-authored HTML whose <script> tags are INERT by innerHTML
      // spec; recreating them here would execute markup sanitizePromoHtml dropped at the
      // source. Only product-card scripts (the documented variant-swatch handler) are re-run.
      if (original.closest('.fs-promo-tile')) continue;
      const type = (original.getAttribute('type') || '').trim().toLowerCase();
      if (!EXECUTABLE_SCRIPT_TYPES.has(type)) continue; // JSON data blobs etc. — leave alone

      const key = original.src || original.textContent;
      if (!key) continue;

      if (executedScripts.has(key)) {
        original.remove(); // already running from an earlier render
        continue;
      }
      executedScripts.add(key);

      const replacement = document.createElement('script');
      for (const { name, value } of original.attributes) replacement.setAttribute(name, value);
      if (!original.src) replacement.textContent = original.textContent;
      original.replaceWith(replacement);
    }
  };

  /* ------------------------------------------------- product-card design transplant */

  /**
   * Fast Simon cards are fetched from the /search template's `fs-search-cards` section, so they
   * arrive wearing the SEARCH template's product-card design — never the collection page's, which
   * is the "product card" block a merchant edits in the theme editor. On a collection PLP we
   * therefore transplant the collection card's design onto every Fast Simon card: the inline
   * border/padding/gap custom properties and the colour-scheme class that snippets/product-card.liquid
   * bakes from the block settings. It is captured from the native cards the section renders (present
   * until Fast Simon first replaces them, and briefly again whenever the editor re-renders the
   * section) and re-applied on `shopify:section:load`, so editing the block styles the Fast Simon
   * cards too.
   *
   * Only the design is copied. The per-product quick-add display flags are left exactly as the
   * fetched card rendered them, because those depend on each product's availability, not the block.
   *
   * @type {{cssText: string, colorClass: string|null}|null}
   */
  let cardDesign = null;

  /** Per-product inline styles that must survive a design transplant. */
  const CARD_DESIGN_KEEP = ['--quick-add-display', '--quick-add-mobile-display'];

  /**
   * Reads the design off a NATIVE collection card in `root`, or null when there is none (e.g. an
   * empty collection in the editor, or after Fast Simon has already replaced every native card —
   * in which case there is nothing new to learn and the previous capture is kept).
   *
   * Native cards are targeted specifically: `main-collection.liquid` stamps each server-rendered
   * `<li>` with `data-page`, which the fetched Fast Simon cards never carry. Capturing from a Fast
   * Simon card would read back the /search design we are trying to overwrite.
   *
   * @param {ParentNode} [root]
   * @returns {{cssText: string, colorClass: string|null}|null}
   */
  const captureCardDesign = (root = grid) => {
    const sample = root.querySelector('.product-grid__item[data-page] .product-card__content');
    if (!(sample instanceof HTMLElement)) return null;
    const colorClass = [...sample.classList].find((c) => c.startsWith('color-')) || null;
    return { cssText: sample.getAttribute('style') || '', colorClass };
  };

  /**
   * Applies the captured collection design to one Fast Simon card's content element, preserving
   * that card's own quick-add flags.
   *
   * @param {HTMLElement} content `.product-card__content` of a Fast Simon card
   */
  const applyCardDesign = (content) => {
    if (!cardDesign || !(content instanceof HTMLElement)) return;

    const kept = {};
    for (const prop of CARD_DESIGN_KEEP) kept[prop] = content.style.getPropertyValue(prop);

    content.setAttribute('style', cardDesign.cssText);

    for (const prop of CARD_DESIGN_KEEP) {
      if (kept[prop]) content.style.setProperty(prop, kept[prop]);
      else content.style.removeProperty(prop);
    }

    // The colour scheme is a class, not part of the style attribute.
    for (const c of [...content.classList]) {
      if (c.startsWith('color-')) content.classList.remove(c);
    }
    if (cardDesign.colorClass) content.classList.add(cardDesign.colorClass);
  };

  /**
   * Applies the captured design to every Fast Simon card currently in `root`.
   *
   * @param {ParentNode} [root]
   */
  const applyCardDesignToGrid = (root = grid) => {
    if (!cardDesign) return;
    for (const content of root.querySelectorAll('.product-grid__item .product-card__content')) {
      applyCardDesign(content);
    }
  };

  /**
   * Reports an event by the first name the loaded SDK build actually exposes. Several names are
   * documented but absent from SDK v1.6.x on this store — `SearchWithinPerformed`, and the
   * `Performed`/`Preformed` spelling of the collection event — so each case lists the candidates
   * in order of preference and falls back to one the build has rather than dropping the data.
   *
   * `reportAppType` forces an immediate send (the "SPA" app type) for actions that happen in place
   * with no navigation — a quick view, like an add-to-cart — so the report is not deferred to the
   * next page initialization under this store's MPA config. It is omitted for the listing-view and
   * click events, which accompany a real navigation.
   *
   * @param {string|string[]} name one event name, or names in order of preference
   * @param {object} data
   * @param {string} [reportAppType] optional AppType override ("SPA" | "MPA")
   */
  const fsEvent = (name, data, reportAppType) => {
    // Before readiness there is no Fast Simon listing on screen to attribute to (the takeover
    // itself waits on sdkReady), and a premature send can be lost under this store's MPA
    // config — drop, exactly as the previous onReady-injected controller did for clicks in
    // the gap before it loaded.
    if (!sdkReady) return;
    const names = window.FastSimonEventName;
    if (!names) return;
    const candidate = (Array.isArray(name) ? name : [name]).find((n) => names[n] != null);
    if (!candidate) return;
    try {
      const report = { eventName: names[candidate], data };
      if (reportAppType) report.reportAppType = reportAppType;
      window.FastSimonSDK.event(report);
    } catch (error) {
      console.warn('[Fast Simon] event failed', candidate, error);
    }
  };

  /**
   * Fast Simon product id by handle, filled while rendering. A swatch switch is
   * handled by the theme's own card script, which only knows the handle of the
   * colour it is moving to — this is what lets that event carry the same product
   * ids as every other event on this page.
   * @type {Map<string, string>}
   */
  const fsIdByHandle = new Map();

  /* ------------------------------------------------------------------ routing */

  /**
   * URL contract: `?q=<term>&page=<n>&sort=<fs key>&fsf=<Group>:<Value>&fsw=<within>`.
   * `fsf` repeats, one per active filter value.
   */
  const readUrl = () => {
    const params = new URLSearchParams(window.location.search);
    state.term = params.get('q') || '';
    state.page = Math.max(1, parseInt(params.get('page'), 10) || 1);
    // A hand-edited or stale `?sort=` outside the documented SortBy set is dropped to the
    // dashboard default rather than sent to the SDK as an invalid sortBy.
    const sortParam = params.get('sort');
    state.sortBy =
      sortParam && Object.prototype.hasOwnProperty.call(SORT_LABELS, sortParam) ? sortParam : null;
    state.searchWithin = params.get('fsw') || '';
    state.narrowBy = params
      .getAll('fsf')
      .map((pair) => {
        const idx = pair.indexOf(':');
        return idx === -1 ? null : [pair.slice(0, idx), pair.slice(idx + 1)];
      })
      .filter(Boolean);

    // Collection URLs in the wild — bookmarks, old marketing links, feeds, the sitemap — sort with
    // Shopify's own `sort_by`. Honouring one costs a lookup and keeps those links sorting the way
    // shoppers and the merchant expect; ignoring it would silently change every one of them to the
    // Fast Simon dashboard default.
    if (!state.sortBy && isCollection) {
      const shopifySort = params.get('sort_by');
      if (shopifySort && Object.prototype.hasOwnProperty.call(SHOPIFY_SORT_MAP, shopifySort)) {
        state.sortBy = SHOPIFY_SORT_MAP[shopifySort];
      }
    }
  };

  const writeUrl = (replace) => {
    // On a collection the query string is not ours to own: utm parameters, ?variant= deep links and
    // discount codes must survive a filter change untouched, so only the keys this controller
    // manages are rewritten. On /search the query string IS the search, so it is rebuilt.
    const params = isCollection ? new URLSearchParams(window.location.search) : new URLSearchParams();

    if (!isCollection && state.term) params.set('q', state.term);
    if (state.page > 1) params.set('page', String(state.page));
    else params.delete('page');
    if (state.sortBy) params.set('sort', state.sortBy);
    else params.delete('sort');
    if (state.searchWithin) params.set('fsw', state.searchWithin);
    else params.delete('fsw');

    // Native Shopify filtering is not rendered on a Fast Simon listing, so a leftover native filter
    // or sort in the URL would describe a grid the shopper is not looking at. Fast Simon's own
    // `fsf` list is cleared and rebuilt from state each time.
    if (isCollection) {
      for (const key of [...params.keys()]) {
        if (
          key === 'fsf' ||
          key === 'sort_by' ||
          key === 'type' ||
          key === 'vendor' ||
          key.startsWith('filter.') ||
          key.startsWith('options.')
        ) {
          params.delete(key);
        }
      }
    }

    for (const [group, value] of state.narrowBy) params.append('fsf', `${group}:${value}`);
    // Collections deep-link with `#<section id>-<product id>` when a shopper returns from a
    // product page; a rewritten URL that drops the fragment also drops that anchor for anything
    // that reads the address later (back/forward, share, analytics).
    const hash = window.location.hash || '';
    const url = `${window.location.pathname}?${params.toString()}${hash}`;
    window.history[replace ? 'replaceState' : 'pushState']({ fs: true }, '', url);
  };

  /* ------------------------------------------------------------- card fetching */

  /**
   * Ask Liquid to render native product cards for `handles`.
   * `type=article` keeps Shopify's own (unused) search cheap; only `search.terms` is read.
   *
   * With a known section id this returns just the fragment; without one it returns the whole
   * /search page and the cards are read out of that (see cardsSectionId above).
   *
   * @param {string[]} handles
   * @param {AbortSignal} signal
   * @returns {Promise<Map<string, HTMLLIElement>>}
   */
  // Short-lived, bounded, document-local markup cache. Never reuse live DOM nodes.
  const cardCache = new Map();
  const CARD_CACHE_TTL_MS = 30000;
  const CARD_CACHE_LIMIT = 120;
  let cardCacheContext = '';

  const fetchCards = async (handles, signal) => {
    const byHandle = new Map();
    if (!handles.length) return byHandle;
    const context = JSON.stringify([
      searchUrl, document.documentElement.lang,
      window.Shopify?.theme?.id, window.Shopify?.currency?.active,
      window.Shopify?.country, window.location.host,
    ]);
    if (context !== cardCacheContext) {
      cardCache.clear();
      cardCacheContext = context;
    }
    const now = Date.now();
    const uniqueHandles = [...new Set(handles)].filter(handle => {
      const cached = cardCache.get(handle);
      if (!cached || now - cached.at > CARD_CACHE_TTL_MS) {
        cardCache.delete(handle);
        return true;
      }
      byHandle.set(handle, cached.card.cloneNode(true));
      return false;
    });
    const groups = chunk(uniqueHandles, CARD_CHUNK_SIZE);
    const requestGroup = async (group) => {
      const chunkCards = new Map();
      const q = `${CARD_TOKEN} ${group.join(' ')}`;

      const fetchChunk = async (sectionId) => {
        const sectionParam = sectionId ? `&section_id=${encodeURIComponent(sectionId)}` : '';
        const url = `${searchUrl}?q=${encodeURIComponent(q)}&type=article${sectionParam}`;
        const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`card chunk ${response.status}`);
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      };

      const collect = (doc) => {
        for (const li of doc.querySelectorAll('li.product-grid__item')) {
          const handle = li.dataset.productHandle;
          if (handle && group.includes(handle)) chunkCards.set(handle, li);
        }
        rememberCardsSectionId(doc);
      };

      /*
       * A stale id — the merchant renamed or re-added the fs-search-cards section, or a cached
       * sessionStorage value points at one that is gone — usually renders a page with no cards
       * rather than an error, so the grid would silently stay empty. Fall back to the whole page,
       * which relearns the id from the marker, instead of showing a blank PLP. Some missing-section
       * responses surface as an HTTP error rather than an empty page, so the sectioned fetch is
       * guarded: a throw other than an abort (an abort is a newer request superseding this one and
       * must never be retried) is treated like the empty case and routed into the same whole-page
       * fallback below.
       */
      if (cardsSectionId) {
        try {
          collect(await fetchChunk(cardsSectionId));
        } catch (error) {
          if (signal.aborted) throw error;
        }
        if (!chunkCards.size) {
          cardsSectionId = '';
          safeStorage(() => window.sessionStorage.removeItem(CARDS_ID_KEY));
          collect(await fetchChunk(''));
        }
      } else {
        collect(await fetchChunk(''));
      }
      return chunkCards;
    };

    /*
     * First render of a session: with no section id known yet, EVERY chunk would download
     * the whole /search page (two full pages for a 40-product PLP). Let only the first
     * chunk do that — its response harvests the section id via rememberCardsSectionId —
     * and start the remaining chunks once it has settled, so they get the cheap fragment.
     * A response without the marker leaves the id empty and the rest fall back exactly as
     * before. Id already known (every later render): all chunks stay parallel.
     */
    let requests;
    if (groups.length <= 1 || cardsSectionId) {
      // No harvest needed (id known), or at most one chunk — nothing to serialise.
      requests = groups.map((group) => requestGroup(group));
    } else {
      const first = requestGroup(groups[0]);
      requests = [first, ...groups.slice(1).map((group) => first.then(() => requestGroup(group)))];
    }
    const chunks = await Promise.all(requests);
    for (const cards of chunks) {
      for (const [handle, card] of cards) {
        byHandle.set(handle, card);
        // Do not let a response from an earlier currency/context refill this cache.
        if (!signal.aborted && context === cardCacheContext) {
          cardCache.delete(handle);
          cardCache.set(handle, { at: Date.now(), card: card.cloneNode(true) });
          while (cardCache.size > CARD_CACHE_LIMIT) {
            cardCache.delete(cardCache.keys().next().value);
          }
        }
      }
    }
    return byHandle;
  };

  /**
   * @param {object} tile a Fast Simon promo tile
   * @param {number} slot the tile's 1-based position among everything shown in the grid
   */
  const buildPromoTile = (tile, slot) => {
    const li = document.createElement('li');
    li.className = 'product-grid__item fs-promo-tile';
    if (tile.width > 1) li.dataset.spS = tile.height > 1 ? 'large' : 'horizontal';
    li.dataset.promotileId = tile.id;
    li.dataset.fsPosition = String(slot);
    // Kept on the element because SerpPromoTileClicked reports a tile the way
    // SerpProductClicked reports a product: id, position, link and thumbnail.
    // Only a safeUrl-approved link is kept — a rejected scheme must not surface anywhere.
    const href = safeUrl(tile.link);
    if (href) li.dataset.promotileLink = href;
    li.dataset.promotileThumbnail = tile.image || tile.mobile_image || '';

    if (tile.html) {
      li.innerHTML = sanitizePromoHtml(tile.html);
      return li;
    }
    const useMobile = tile.mobile_image && window.matchMedia('(max-width: 749px)').matches;
    const image = useMobile ? tile.mobile_image : tile.image;
    const img = `<img class="fs-promo-tile__image" src="${esc(image)}" alt="${esc(tile.title || '')}" loading="lazy">`;
    li.innerHTML = href ? `<a class="fs-promo-tile__link" href="${esc(href)}">${img}</a>` : img;
    return li;
  };

  /* ----------------------------------------------------------------- rendering */

  const setBusy = (busy) => {
    if (!busy) {
      window.clearTimeout(requestTimer);
      requestTimer = null;
    }
    gridWrapper.toggleAttribute('data-fs-loading', busy);
    grid.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) gridWrapper.querySelector('[data-fs-pagination]')?.remove();
  };


  const renderEmpty = () => {
    grid.replaceChildren();
    let empty = gridWrapper.querySelector('.main-collection-grid__empty');
    if (!empty) {
      empty = document.createElement('div');
      empty.className = 'main-collection-grid__empty';
      empty.innerHTML = `<h2 class="main-collection-grid__empty-title h2">${esc(emptyLabel)}</h2>`;
      gridWrapper.prepend(empty);
    }
    empty.hidden = false;
    empty.style.removeProperty('display');
    performance.mark('fs-results:commit');
  };

  const clearEmpty = () => {
    const empty = gridWrapper.querySelector('.main-collection-grid__empty');
    if (empty) {
      empty.hidden = true;
      empty.style.display = 'none';
    }
  };

  /**
   * @param {object[]} products full Fast Simon products array (promo tiles included)
   * @param {AbortSignal} signal
   */
  const renderProducts = async (products, signal) => {
    const realProducts = products.filter((p) => !p.promotile);
    if (!products.length) {
      renderEmpty();
      // The grid now shows this listing's (empty) result — re-capture attribution so a later
      // click is filed against it, not the previous listing.
      commitAttribution();
      return;
    }
    clearEmpty();

    const handles = [];
    const handleFor = new Map();
    // Build the incoming listing's handle→id map locally and publish it only when these cards
    // actually commit to the grid (below). Mutating the shared fsIdByHandle here would mean that,
    // if this fetch is aborted or fails, the OLD cards still on screen would resolve swatches
    // against the INCOMING listing's map — attributing a swatch click to a product not shown.
    const nextFsIdByHandle = new Map();
    for (const product of realProducts) {
      const handle = handleFromUrl(product.u);
      if (!handle) continue;
      handles.push(handle);
      handleFor.set(product.id, handle);
      nextFsIdByHandle.set(handle, product.id);
    }

    const cards = await fetchCards(handles, signal);
    if (signal.aborted) return;

    // Every expected card missing: committing would blank the grid while the request reports
    // success — the per-handle skip below only covers stragglers (unpublished handles), never
    // this. Fail instead: the catch in onProductsLoaded routes to failRequest, which keeps the
    // previous grid on screen and offers recovery.
    if (realProducts.length && !cards.size) {
      throw new Error('Fast Simon returned products but no product cards could be rendered');
    }

    // Partial misses happen (an unpublished handle renders no card, a response can come back
    // short) but must leave a trace: which handles expected a card and got none, with the
    // cause deliberately NOT assumed — "missing", not "unavailable" — as the diagnostic for a
    // listing that renders short. Total failure is the throw above.
    const missing = handles.filter((handle) => !cards.has(handle));
    if (missing.length) {
      console.warn(
        `[Fast Simon] ${missing.length} of ${handles.length} product cards missing from the response`,
        missing
      );
    }

    const fragment = document.createDocumentFragment();
    let position = 0;
    let slot = 0;

    for (const product of products) {
      slot += 1;
      if (product.promotile) {
        fragment.appendChild(buildPromoTile(product, slot));
        continue;
      }
      const card = cards.get(handleFor.get(product.id));
      if (!card) continue; // handle unpublished or not found — skip rather than leave a hole

      position += 1;
      card.id = `${gridSectionId}-${product.id}`;
      if (product.sp_s) card.dataset.spS = product.sp_s;
      card.dataset.fsPosition = String(position);
      fragment.appendChild(card);
    }

    grid.replaceChildren(fragment);

    // The new cards are now the visible grid — publish their handle→id map (built above) and
    // re-capture attribution to match them, together. Until this commit the old cards keep
    // resolving against the previous listing's map, so a swatch click during the fetch window is
    // attributed correctly, including when this request ultimately fails and the old cards stay.
    fsIdByHandle.clear();
    for (const [handle, id] of nextFsIdByHandle) fsIdByHandle.set(handle, id);

    // The new cards are now the visible grid — re-capture attribution to match them. A click
    // from here until the next commit belongs to THIS listing, even if `state` has already moved
    // on to a newer request whose cards have not rendered yet.
    commitAttribution();

    // Must run after insertion: a re-created script only executes once it is in the document.
    runInjectedScripts(grid);

    // Fast Simon cards arrived styled for the /search template's card block; on a collection PLP
    // re-dress them in the collection page's own card design (captured before this first render).
    if (isCollection) applyCardDesignToGrid(grid);

    // Impressions are attached after the nodes are live so the SDK can observe them.
    if (typeof window.FastSimonSDK.trackViewportImpression === 'function') {
      for (const card of grid.querySelectorAll('.product-grid__item[data-product-id]')) {
        if (card.dataset.fsImpressionBound === '1') continue;
        card.dataset.fsImpressionBound = '1';
        window.FastSimonSDK.trackViewportImpression(
          card,
          card.dataset.productId,
          parseInt(card.dataset.fsPosition, 10) || 1
        );
      }
    }

    /** Takeover complete — fs-results:commit − fs-results:run is the full takeover cost. */
    performance.mark('fs-results:commit');
  };

  /* -------------------------------------------------------------------- facets */

  const isActive = (group, value) =>
    state.narrowBy.some(([g, v]) => g === group && String(v) === String(value));

  /* --------------------------------------------------------------- price range */

  const moneyFormatter = (() => {
    const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (!currency) return null;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      });
    } catch (error) {
      return null;
    }
  })();

  const money = (amount) => (moneyFormatter ? moneyFormatter.format(amount) : String(amount));

  /** Slider bounds from the `Price_min` / `Price_max` facets, or null when unusable. */
  const readPriceBounds = (facets) => {
    const pick = (key) => {
      const facet = (facets || []).find((f) => f[0] === key);
      if (!facet || !Array.isArray(facet[1]) || !facet[1].length) return null;
      const n = Number(facet[1][0]);
      return Number.isFinite(n) ? n : null;
    };
    const min = pick('Price_min');
    const max = pick('Price_max');
    if (min === null || max === null) return null;
    const bounds = { min: Math.floor(min), max: Math.ceil(max) };
    return bounds.max > bounds.min ? bounds : null;
  };

  /** Parses a `"200-400"` narrowBy value. */
  const parsePriceValue = (value) => {
    const match = /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/.exec(String(value));
    return match ? { from: Number(match[1]), to: Number(match[2]) } : null;
  };

  const activePrice = () => {
    const entry = state.narrowBy.find(([g]) => g === PRICE_KEY);
    return entry ? parsePriceValue(entry[1]) : null;
  };

  const priceChipLabel = (value) => {
    const range = parsePriceValue(value);
    return range ? `${money(range.from)} – ${money(range.to)}` : String(value);
  };

  const pricePercent = (value, min, max) => ((value - min) / (max - min)) * 100;

  /**
   * Builds the price group: same accordion shell as buildFacetGroup, with a two-thumb range
   * slider (two native range inputs sharing one track) instead of a checkbox list.
   */
  const buildPriceGroup = (bounds, label, scope) => {
    const id = `${scope}-price-range`;
    const isDrawer = scope === 'drawer';
    const selected = activePrice();

    // Other filters can shrink the bounds below an already-selected range; widen the scale
    // so the thumbs always sit on it.
    const min = selected ? Math.min(bounds.min, Math.floor(selected.from)) : bounds.min;
    const max = selected ? Math.max(bounds.max, Math.ceil(selected.to)) : bounds.max;
    const from = selected ? selected.from : min;
    const to = selected ? selected.to : max;

    const input = (which, value) => `<input
        type="range"
        class="fs-price-range__input${which === 'from' && value > (min + max) / 2 ? ' fs-price-range__input--top' : ''
      }"
        id="fs-price-${esc(id)}-${which}"
        data-fs-price="${which}"
        min="${min}" max="${max}" step="1" value="${value}"
        aria-label="${which === 'from' ? 'Minimum price' : 'Maximum price'}"
        aria-valuetext="${esc(money(value))}"
      >`;

    const body = `<div class="facets__inputs-wrapper">
        <div
          class="fs-price-range"
          data-fs-price-range
          data-min="${min}"
          data-max="${max}"
          style="--fs-price-lo: ${pricePercent(from, min, max)}%; --fs-price-hi: ${pricePercent(to, min, max)}%;"
        >
          <div class="fs-price-range__values" aria-hidden="true">
            <span data-fs-price-text="from">${esc(money(from))}</span>
            <span data-fs-price-text="to">${esc(money(to))}</span>
          </div>
          <div class="fs-price-range__track">
            ${input('from', from)}
            ${input('to', to)}
          </div>
        </div>
      </div>`;

    return `<accordion-custom
        class="facets__item"
        ${isDrawer ? 'open-by-default-on-mobile open-by-default-on-desktop' : 'data-disable-animation-on-desktop="true" data-close-with-escape="true" open-by-default-on-mobile'}
      >
        <details id="fs-facet-details-${esc(id)}" class="facets__panel" ${isDrawer ? 'open' : 'data-auto-close-details="desktop"'
      }>
          <summary class="facets__summary">
            <span class="facets__label">${esc(label)}</span>
            <div class="facets__status-wrapper">
              <span class="facets__status">${selected ? '1' : ''}</span>
              <span class="svg-wrapper icon-caret icon-animated">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </summary>
          ${isDrawer

        ? `<div class="facets__inputs facets__panel-content details-content" id="fs-facet-inputs-${esc(id)}">${body}</div>`
        : `<floating-panel-component
                   data-close-on-resize
                   class="facets__inputs facets__panel-content details-content"
                   id="fs-facet-inputs-${esc(id)}"
                 >${body}</floating-panel-component>`
      }
        </details>
      </accordion-custom>`;
  };

  /**
   * Keeps the two thumbs from crossing and refreshes the labels and the filled track.
   * Runs on every `input` event — DOM writes on one small subtree, no network.
   * @returns {{from: number, to: number, min: number, max: number} | null}
   */
  const syncPrice = (input) => {
    const root = input.closest('[data-fs-price-range]');
    if (!root) return null;
    const fromEl = root.querySelector('[data-fs-price="from"]');
    const toEl = root.querySelector('[data-fs-price="to"]');
    if (!fromEl || !toEl) return null;

    const min = Number(root.dataset.min);
    const max = Number(root.dataset.max);
    let from = Number(fromEl.value);
    let to = Number(toEl.value);
    if (from > to) {
      if (input === fromEl) from = to;
      else to = from;
      fromEl.value = String(from);
      toEl.value = String(to);
    }

    root.style.setProperty('--fs-price-lo', `${pricePercent(from, min, max)}%`);
    root.style.setProperty('--fs-price-hi', `${pricePercent(to, min, max)}%`);
    // When both thumbs sit at the top of the scale the "from" thumb must be the one on top,
    // otherwise neither can be dragged back down.
    fromEl.classList.toggle('fs-price-range__input--top', from > (min + max) / 2);
    fromEl.setAttribute('aria-valuetext', money(from));
    toEl.setAttribute('aria-valuetext', money(to));
    const fromText = root.querySelector('[data-fs-price-text="from"]');
    const toText = root.querySelector('[data-fs-price-text="to"]');
    if (fromText) fromText.textContent = money(from);
    if (toText) toText.textContent = money(to);

    return { from, to, min, max };
  };

  /** Applies a released slider value. Debounced so holding an arrow key sends one search. */
  const schedulePriceCommit = (range) => {
    window.clearTimeout(priceCommitTimer);
    priceCommitTimer = window.setTimeout(() => {
      priceCommitTimer = null;
      pendingFacets = null;
      const rest = state.narrowBy.filter(([g]) => g !== PRICE_KEY);
      const isFullRange = range.from <= range.min && range.to >= range.max;
      const next = isFullRange ? rest : rest.concat([[PRICE_KEY, `${range.from}-${range.to}`]]);

      const before = state.narrowBy.find(([g]) => g === PRICE_KEY);
      const after = next.find(([g]) => g === PRICE_KEY);
      if ((before ? before[1] : null) === (after ? after[1] : null)) return;

      state.narrowBy = next;
      rerunFromFilters();
    }, DEBOUNCE_MS);
  };

  /**
   * Builds one facet group using the same structure and class names as
   * snippets/list-filter.liquid, so the theme's facet CSS styles it unchanged.
   * `accordion-custom` and `floating-panel-component` are reused deliberately: they are
   * presentation-only, and they are the two tags the theme styles by element name.
   */
  const buildFacetGroup = (facet, scope) => {
    const [key, values, label] = facet;
    // The hash keeps ids distinct when slug() collapses labels ("A/B" vs "A B"); deterministic,
    // so openPanels and focus restoration still resolve against the rebuilt markup.
    const id = `${scope}-${slug(key)}-${idHash(key)}`;
    const isDrawer = scope === 'drawer';
    const activeCount = state.narrowBy.filter(([g]) => g === key).length;
    const asSwatches = isSwatchFacet(key, label);

    const items = values
      .map(([value]) => {
        // Scoped so the desktop and drawer copies never share an id — a duplicate id would
        // point every <label for> at the first copy and break the drawer's checkboxes. The
        // value hash does the same for distinct values slug() collapses ("A/B" vs "A B").
        const inputId = `fs-filter-${id}-${slug(value)}-${idHash(value)}`;
        const checked = isActive(key, value) ? 'checked' : '';
        const data = `data-fs-facet-group="${esc(key)}" data-fs-facet-value="${esc(value)}"`;

        if (asSwatches) {
          const colour = swatchColour(value);
          return `<li class="facets__inputs-list-item">
              <fieldset class="variant-option variant-option--buttons variant-option--swatches" aria-label="${esc(value)}">
                <label class="variant-option__button-label variant-option__button-label--has-swatch" title="${esc(value)}">
                  <div class="variant-option__swatch-wrapper">
                    <input tabindex="0" type="checkbox" id="${esc(inputId)}" aria-label="${esc(value)}" ${data} ${checked}>
                    <span class="swatch swatch--filter${colour ? '' : ' swatch--unavailable'}" style="--swatch-background: ${colour};"></span>
                  </div>
                  <span class="visually-hidden">${esc(value)}</span>
                </label>
              </fieldset>
            </li>`;
        }

        return `<li class="facets__inputs-list-item">
            <div class="checkbox">
              <input
                type="checkbox"
                id="${esc(inputId)}"
                class="checkbox__input"
                ${data}
                ${checked}
              >
              <label class="checkbox__label !rounded-none" for="${esc(inputId)}">
                ${checkmarkSvg()}
                <span class="checkbox__label-text">${esc(value)}</span>
              </label>
            </div>
          </li>`;
      })
      .join('');

    return `<accordion-custom
        class="facets__item"
        ${isDrawer ? 'open-by-default-on-mobile open-by-default-on-desktop' : 'data-disable-animation-on-desktop="true" data-close-with-escape="true" open-by-default-on-mobile'}
      >
        <details id="fs-facet-details-${esc(id)}" class="facets__panel" ${isDrawer ? 'open' : 'data-auto-close-details="desktop"'
      }>
          <summary class="facets__summary">
            <span class="facets__label">${esc(label || key)}</span>
            <div class="facets__status-wrapper">
              <span class="facets__status">${activeCount ? esc(activeCount) : ''}</span>
              <span class="svg-wrapper icon-caret icon-animated">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </summary>
          ${isDrawer
        ? `<div class="facets__inputs facets__panel-content details-content" id="fs-facet-inputs-${esc(id)}">
                   <div class="facets__inputs-wrapper${asSwatches ? ' facets__inputs-wrapper--row' : ''}">
                     <ul class="facets__inputs-list list-unstyled${asSwatches ? ' facets__inputs-list--swatches' : ''}">${items}</ul>
                   </div>
                 </div>`
        : `<floating-panel-component
                   data-close-on-resize
                   class="facets__inputs facets__panel-content details-content"
                   id="fs-facet-inputs-${esc(id)}"
                 >
                   <div class="facets__inputs-wrapper${asSwatches ? ' facets__inputs-wrapper--row' : ''}">
                     <ul class="facets__inputs-list list-unstyled${asSwatches ? ' facets__inputs-list--swatches' : ''}">${items}</ul>
                   </div>
                 </floating-panel-component>`
      }
        </details>
      </accordion-custom>`;
  };

  const renderChips = () => {
    // Active-filter count bubble on the mobile toggle and in the drawer title.
    for (const el of filterCountEls) {
      el.hidden = state.narrowBy.length === 0;
      const text = el.querySelector('[data-fs-filter-count-text]');
      if (text) text.textContent = String(state.narrowBy.length);
    }

    if (!chipContainers.length) return;
    if (!state.narrowBy.length) {
      for (const el of chipContainers) {
        el.hidden = true;
        el.innerHTML = '';
      }
      return;
    }
    const html = `${state.narrowBy
      .map(
        ([group, value]) =>
          `<button type="button" class="facets__remove-filter" data-fs-remove-group="${esc(
            group
          )}" data-fs-remove-value="${esc(value)}">
             <span>${esc(group === PRICE_KEY ? priceChipLabel(value) : value)}</span>
             <span class="visually-hidden">Remove filter ${esc(
            group === PRICE_KEY ? priceChipLabel(value) : value
          )}</span>
           </button>`
      )
      .join('')}<button type="button" class="facets__remove-filter fs-remove-all" data-fs-remove-all>Clear all</button>`;

    for (const el of chipContainers) {
      el.hidden = false;
      el.innerHTML = html;
    }
  };

  let warnedUnresolved = false;

  const renderFacets = (facets) => {
    if (!filteringEnabled || !groupContainers.length) return;

    // A response that lands while a thumb is held would rebuild the slider under the
    // shopper's pointer. Park it; the pointerup handler renders it.
    if (priceDragging) {
      pendingFacets = facets;

      return;
    }

    const unresolved = [];
    const renderable = (facets || [])
      .filter((facet) => !SKIPPED_FACETS.has(facet[0]) && Array.isArray(facet[1]) && facet[1].length)
      .map((facet) => {
        const [key, values, ...rest] = facet;
        const clean = values.filter(([value]) => {
          if (!UNRESOLVED_VALUE.test(String(value))) return true;
          unresolved.push(`${key} = ${value}`);
          return false;
        });
        return [key, clean, ...rest];
      })
      // A facet whose every value was unresolved has nothing left to offer.
      .filter((facet) => facet[1].length);

    if (unresolved.length && !warnedUnresolved) {
      warnedUnresolved = true;
      console.warn(
        '[Fast Simon] Hid unresolved metaobject facet values. Fix in the Fast Simon dashboard ' +
        '(point the facet at the .label attribute, or re-sync):',
        unresolved
      );
    }

    // The slider takes the slot the dashboard gave the Price filter.
    // A zero-result response carries no facets at all. If the price range is what emptied
    // the page, keep the slider on its last known scale so the shopper can widen it again.
    const freshBounds = readPriceBounds(facets);
    if (freshBounds) lastPriceBounds = freshBounds;
    const priceBounds = freshBounds || (activePrice() ? lastPriceBounds : null);
    const keys = (facets || []).map((facet) => facet[0]);
    const priceFacet = (facets || []).find((facet) => facet[0] === 'Price');
    const priceLabel = (priceFacet && priceFacet[2]) || 'Price';
    const pricePosition = keys.indexOf(priceFacet ? 'Price' : 'Price_min');
    const priceIndex = renderable.filter((facet) => keys.indexOf(facet[0]) < pricePosition).length;

    for (const container of groupContainers) {
      const scope = container.dataset.fsFacetGroups || 'desktop';

      // Ticking a box re-renders the whole bar; without this the panel the shopper is
      // working in would snap shut on every click.
      const openPanels = new Set(
        [...container.querySelectorAll('details.facets__panel[open]')].map((d) => d.id)
      );

      // A keyboard user adjusting the slider must keep focus across the re-render.
      const active = document.activeElement;
      const focusedPrice =
        active instanceof HTMLElement && container.contains(active) ? active.dataset.fsPrice : '';
      // Same for every other control the rebuild replaces (checkboxes, swatches): their ids
      // are deterministic (idHash), so re-focusing the id restores keyboard/SR position.
      const focusedId =
        !focusedPrice && active instanceof HTMLInputElement && container.contains(active)
          ? active.id
          : '';

      const groups = renderable.map((facet) => buildFacetGroup(facet, scope));
      if (priceBounds) groups.splice(priceIndex, 0, buildPriceGroup(priceBounds, priceLabel, scope));
      container.innerHTML = groups.join('');

      for (const id of openPanels) {
        const panel = container.querySelector(`#${CSS.escape(id)}`);
        if (panel) panel.open = true;
      }

      if (focusedPrice) {
        const input = container.querySelector(`[data-fs-price="${focusedPrice}"]`);
        if (input) input.focus({ preventScroll: true });
      } else if (focusedId) {
        const input = container.querySelector(`#${CSS.escape(focusedId)}`);
        if (input) input.focus({ preventScroll: true });
      }
    }
    renderChips();
  };

  const renderSort = () => {
    if (!sortingEnabled || !sortOptionContainers.length) return;
    for (const el of sortWrappers) el.hidden = false;

    for (const container of sortOptionContainers) {
      const scope = container.dataset.fsSortOptions || 'desktop';
      // The "Default" row is the untouched state: it carries no sortBy at all (the dashboard
      // applies its configured default) and is the only honest label for it — the controller
      // cannot know which SortBy that default is, so no configured option is ever presumed to
      // be it. It is also the way back to the default after an explicit sort was picked.
      const defaultId = `fs-sort-${scope}-default`;
      const defaultRow = `<label for="${defaultId}">
            <div class="sorting-filter__option" role="option" aria-selected="${!state.sortBy}">
              <input type="radio" name="fs_sort_by_${esc(scope)}" id="${defaultId}" value="" class="sorting-filter__input" ${state.sortBy ? '' : 'checked'} aria-hidden="true">
              <span class="svg-wrapper icon-checkmark sorting-filter__checkmark"></span>
              <span class="sorting-filter__label">${DEFAULT_SORT_LABEL}</span>
            </div>
          </label>`;
      container.innerHTML = defaultRow + sortKeys
        .map((key) => {
          const label = SORT_LABELS[key] || key;
          const inputId = `fs-sort-${scope}-${slug(key)}`;
          return `<label for="${inputId}">
            <div class="sorting-filter__option" role="option" aria-selected="${key === state.sortBy}">
              <input type="radio" name="fs_sort_by_${esc(scope)}" id="${inputId}" value="${esc(
            key
          )}" class="sorting-filter__input" ${key === state.sortBy ? 'checked' : ''} aria-hidden="true">
              <span class="svg-wrapper icon-checkmark sorting-filter__checkmark"></span>
              <span class="sorting-filter__label">${esc(label)}</span>
            </div>
          </label>`;
        })
        .join('');
    }
    setSortLabel(state.sortBy ? SORT_LABELS[state.sortBy] || state.sortBy : DEFAULT_SORT_LABEL);
  };

  const setSortLabel = (text) => {
    for (const el of sortCurrentEls) el.textContent = text;
  };

  const renderCount = () => {
    const n = state.totalResults;
    const label = `${n.toLocaleString()} ${n === 1 ? 'item' : 'items'}`;
    for (const el of countEls) el.textContent = label;
    // Drawer footer: "See 1,091 products"
    for (const el of seeResultsEls) el.textContent = n > 0 ? `See ${label}` : emptyLabel;
  };

  /* ---------------------------------------------------------------- pagination */

  const getPageUrl = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return url.toString();
  };

  const getPageNumbers = (currentPage, lastPage) => {
    if (lastPage <= 7) return Array.from({ length: lastPage }, (_, i) => i + 1);
    const pages = new Set([1, lastPage]);
    for (let i = Math.max(2, currentPage - 2); i <= Math.min(lastPage - 1, currentPage + 2); i++) {
      pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  };

  // Match PaginatedList's existing numbered pager classes and full-page links.
  // Never use Shopify's initial data-last-page as the Fast Simon page count.
  const renderPagination = () => {
    gridWrapper.querySelector('[data-fs-pagination]')?.remove();
    if (state.pageCount <= 1) return;
    const nav = document.createElement('nav');
    nav.className = 'paginated-list__pagination list';
    nav.dataset.fsPagination = '';
    nav.setAttribute('aria-label', 'Product pagination');

    const directionLink = (text, page, enabled, direction) => {
      const link = document.createElement('a');
      link.textContent = text;
      link.className = `paginated-list__page-link hide-mobile ${direction}`;
      if (enabled) {
        link.href = getPageUrl(page);
        link.rel = direction;
      } else {
        link.setAttribute('aria-disabled', 'true');
      }
      return link;
    };

    const list = document.createElement('ol');
    list.className = 'paginated-list__page-numbers';
    for (const page of getPageNumbers(state.page, state.pageCount)) {
      const li = document.createElement('li');
      if (page === null) {
        const gap = document.createElement('span');
        gap.textContent = '…';
        gap.className = 'paginated-list__page-ellipsis';
        gap.setAttribute('aria-hidden', 'true');
        li.appendChild(gap);
      } else {
        const link = document.createElement('a');
        link.href = getPageUrl(page);
        link.textContent = String(page);
        link.className = 'paginated-list__page-number';
        link.setAttribute('aria-label', `Page ${page}`);
        if (page === state.page) {
          li.classList.add('is-current');
          link.classList.add('is-current');
          link.setAttribute('aria-current', 'page');
        }
        li.appendChild(link);
      }
      list.appendChild(li);
    }
    nav.append(
      directionLink('Previous', state.page - 1, state.page > 1, 'prev'),
      list,
      directionLink('Next', state.page + 1, state.page < state.pageCount, 'next')
    );
    gridWrapper.appendChild(nav);
  };

  /* ------------------------------------------------------------------- search */

  // Recovery is explicit: retain any rendered cards and offer a retry.
  // This is not a native-filter fallback; native facets are not present here.
  const failRequest = (seq, options, error) => {
    if (seq !== requestSeq) return;
    ++requestSeq; // Invalidate late SDK callbacks before cancelling card fetches.
    if (cardsAbort) cardsAbort.abort();
    setBusy(false);
    console.error('[Fast Simon] results failed', error);
    gridWrapper.querySelector('[data-fs-error]')?.remove();
    const notice = document.createElement('div');
    notice.dataset.fsError = '';
    notice.setAttribute('role', 'alert');
    const text = document.createElement('p');
    text.textContent = 'Unable to update products. Please try again.';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button-secondary';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => run(options));
    notice.append(text, retry);
    gridWrapper.appendChild(notice);
  };

  /**
   * The request itself — every caller must go through run() below, which gates on readiness.
   * @param {{page?: number}} [options]
   */
  const runNow = (options = {}) => {
    const { page = state.page } = options;
    performance.mark('fs-results:run');
    const requestedPage = page;
    const seq = ++requestSeq;

    if (cardsAbort) cardsAbort.abort();
    cardsAbort = new AbortController();
    const signal = cardsAbort.signal;

    window.clearTimeout(requestTimer);
    gridWrapper.querySelector('[data-fs-error]')?.remove();
    setBusy(true);
    const retryOptions = { page: requestedPage };
    requestTimer = window.setTimeout(() => {
      failRequest(seq, retryOptions, new Error('Results request timed out'));
    }, REQUEST_TIMEOUT_MS);

    const request = {
      page: requestedPage,
      productsPerPage: perPage,
      narrowBy: state.narrowBy,
      facets: filteringEnabled,
      // Cards come from Liquid, so the extra attribute payload is pure latency here.
      withAttributes: false,
      onFacetsLoaded: (response) => {
        if (seq !== requestSeq) return;
        try {
          renderFacets(response.facets);
        } catch (error) {
          failRequest(seq, retryOptions, error);
        }
      },
      onProductsLoaded: async (response) => {
        if (seq !== requestSeq) return;

        // Turbolink redirect — handle before rendering anything. The target comes from the
        // Fast Simon response: follow it only when safeUrl approves the scheme, otherwise
        // fall through and render the results normally instead of navigating away.
        if (response.turbolink && response.turbolink.u) {
          const redirect = safeUrl(response.turbolink.u);
          if (redirect) {
            window.location.href = redirect;
            return;
          }
        }

        state.totalResults = response.totalResults || 0;
        state.pageCount = response.pageCount || 1;
        renderCount();

        const products = response.products || [];

        // Report the listing as soon as Fast Simon answers, NOT after the cards render.
        // Card rendering is a second round trip (Section Rendering API) that takes seconds on
        // a 40-product page; reporting behind it meant a shopper who changed a filter twice
        // in quick succession had the first search aborted and never reported, and a failed
        // card fetch lost the event entirely.
        const promoTiles = products
          .filter((p) => p.promotile)
          .map((p) => ({ id: p.id, name: p.title, link: p.link, thumbnail: p.image }));
        const productIds = products.filter((p) => !p.promotile).map((p) => p.id);

        // Both listing events carry the same metrics. What differs is the key that names the
        // surface — `query` on /search, `categoryID` on a collection — and where the search-within
        // term goes: the collection event has no searchWithinSearch field, so the term rides in
        // `query`, which on a collection otherwise defaults to the collection name.
        const metrics = {
          narrowBy: state.narrowBy,
          // Any explicit pick — including 'relevency' — is the shopper's choice and is
          // reported as one; null means untouched, and reporting the dashboard default as a
          // sort would read as a choice nobody made.
          sortBy: state.sortBy || undefined,
          products: productIds.length ? productIds : undefined,
          rescount: String(state.totalResults ?? 0),
          pagenum: String(requestedPage),
          promoTiles: promoTiles.length ? promoTiles : undefined,
        };
        const payload = isCollection
          ? { categoryID, query: state.searchWithin || undefined, ...metrics }
          : {
              query: state.term,
              searchWithinSearch: state.searchWithin || undefined,
              ...metrics,

            };

        fsEvent(state.searchWithin ? SURFACE.viewEventWithin : SURFACE.viewEvent, payload);

        try {
          await renderProducts(products, signal);
        } catch (error) {
          if (seq !== requestSeq) return;
          failRequest(seq, retryOptions, error);
          return;
        }
        if (seq !== requestSeq) return;

        state.page = requestedPage;
        renderPagination();
        setBusy(false);
      },
    };

    // Send any explicit pick, including 'relevency' — a documented SortBy value. When the
    // shopper has not picked one, state.sortBy is null and nothing is sent, so Fast Simon
    // applies the dashboard default. Sending explicit 'relevency' is safe either way: if it
    // IS the default it is a no-op; otherwise it is what the shopper asked for — omitting it
    // used to make an explicit "Relevance" pick silently apply the dashboard default.
    if (state.sortBy) request.sortBy = state.sortBy;
    if (state.searchWithin) request.searchWithinSearch = state.searchWithin;

    // The surfaces take a different first key: a term on /search, the collection's Fast Simon
    // category id on a PLP. `term` is not part of smartCollections' contract, so it is added
    // only where it belongs.
    if (isCollection) request.categoryID = categoryID;
    else request.term = state.term;

    const search = window.FastSimonSDK[SURFACE.method];
    if (typeof search !== 'function') {
      failRequest(seq, retryOptions, new Error(`Fast Simon method ${SURFACE.method} is unavailable`));
      return;
    }
    try {
      search.call(window.FastSimonSDK, request);
    } catch (error) {
      if (seq !== requestSeq) return;
      failRequest(seq, retryOptions, error);
    }
  };

  /**
   * The entry point every caller uses. Before the SDK is ready the method call could be
   * dropped without firing its callback, stranding busy-state — so a run in that window is
   * kept (the LAST one; rapid filter taps collapse into one) and executed the moment
   * readiness arrives. Post-readiness this is runNow, unchanged.
   * @param {{page?: number}} [options]
   */
  const run = (options = {}) => {
    if (!sdkReady) {
      pendingRun = options;
      return;
    }
    runNow(options);
  };

  const rerunFromFilters = () => {
    state.page = 1;
    writeUrl(false);
    renderChips();
    run();
  };

  /* ------------------------------------------------------------------ listeners */

  // One delegated change listener for every facet checkbox and sort radio.
  const onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    // `change` fires when a thumb is released (or on each keyboard step).
    if (target.dataset.fsPrice) {
      const range = syncPrice(target);
      if (range) schedulePriceCommit(range);
      return;
    }

    if (target.dataset.fsFacetGroup) {
      const group = target.dataset.fsFacetGroup;
      const value = target.dataset.fsFacetValue;
      state.narrowBy = target.checked
        ? state.narrowBy.concat([[group, value]])
        : state.narrowBy.filter(([g, v]) => !(g === group && String(v) === String(value)));
      rerunFromFilters();
      return;
    }

    if (target.name && target.name.startsWith('fs_sort_by')) {
      // The "Default" row posts an empty value — the absence of a sortBy, not a sort.
      state.sortBy = target.value || null;
      setSortLabel(target.value ? SORT_LABELS[target.value] || target.value : DEFAULT_SORT_LABEL);
      // Keep the other copy's radios in sync so reopening the drawer shows the right choice.
      for (const radio of $$('input[name^="fs_sort_by"]')) radio.checked = radio.value === target.value;
      rerunFromFilters();
    }
  };

  // Price slider: live feedback while dragging. No request is sent from here.
  const onSliderInput = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.dataset.fsPrice) syncPrice(target);
  };

  const onSliderPointerDown = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.dataset.fsPrice) priceDragging = true;
  };

  const endPriceDrag = () => {
    if (!priceDragging) return;
    priceDragging = false;
    // `change` fires after pointerup, so look again once it has had its turn: if it scheduled
    // a search, that search renders fresh facets and the parked ones are dropped.
    window.setTimeout(() => {
      const facets = pendingFacets;
      pendingFacets = null;
      if (facets && !priceCommitTimer && !priceDragging) renderFacets(facets);
    }, 0);
  };

  // Chip removal.
  const onChipClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-fs-remove-all]')) {
      // A still-pending price commit (slider released < DEBOUNCE_MS ago) would re-add the
      // very filter being cleared — cancel it before wiping the state.
      window.clearTimeout(priceCommitTimer);
      priceCommitTimer = null;
      state.narrowBy = [];
      rerunFromFilters();
      return;
    }
    const chip = event.target.closest('[data-fs-remove-group]');
    if (chip) {
      const group = chip.dataset.fsRemoveGroup;
      const value = chip.dataset.fsRemoveValue;
      // Removing the price chip cancels its pending commit for the same reason. Other chips
      // leave it alone: an independent pending price edit still converges correctly (its own
      // rerun supersedes this one), while cancelling would silently drop the shopper's slider
      // selection they never asked to lose.
      if (group === PRICE_KEY) {
        window.clearTimeout(priceCommitTimer);
        priceCommitTimer = null;
      }
      state.narrowBy = state.narrowBy.filter(([g, v]) => !(g === group && String(v) === String(value)));
      rerunFromFilters();
    }
  };

  // Product click attribution.
  const onGridNavClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const tile = event.target.closest('.fs-promo-tile[data-promotile-id]');
      if (tile) {
        // A promo tile is reported the way a product is: the tile id goes in
        // productID, plus where it sat, where it points and which image showed.
        fsEvent(SURFACE.promoTileClickEvent, {
          ...attribution(),
          productID: tile.dataset.promotileId,
          position: Number(tile.dataset.fsPosition) || 1,
          link: tile.dataset.promotileLink || window.location.href,
          thumbnail: tile.dataset.promotileThumbnail || undefined,
        });
        return;
      }

      // A multi-variant card's quick-add button opens the product in a modal instead of
      // navigating — that is a quick view, its own event, not a click-through to the product.
      // The single-variant quick-add button (`.quick-add__button--add`) submits straight to the
      // cart and is left alone here; its add is already reported by the cart controller. Because
      // the modal opens in place (no navigation), this is sent as "SPA" so it is reported now
      // rather than deferred to the next page init under this store's MPA config.
      const quickView = event.target.closest('.quick-add__button--choose');
      if (quickView) {
        const qvCard = quickView.closest('.product-grid__item[data-product-id]');
        if (qvCard) {
          fsEvent(
            SURFACE.quickViewEvent,
            {
              ...attribution(),
              productID: qvCard.dataset.productId,
              position: parseInt(qvCard.dataset.fsPosition, 10) || 1,
            },
            'SPA'
          );
        }
        return; // a preview is not a click-through — do not also fire the product-click event
      }

      // A colour swatch is its own product here; that move is reported as a swatch
      // switch below, not as a click on the product the tile was showing.
      if (event.target.closest('a[data-mapped-product-url]')) return;

      const card = event.target.closest('.product-grid__item[data-product-id]');
      if (!card) return;

      // A product click means the shopper navigated to the product, so mirror the theme's own
      // navigation rule (product-card.js). The theme binds navigation to the <product-card>
      // element, so a click only navigates when it lands inside one. Gate on that FIRST: a click
      // on the grid item's own padding/margins — outside the <product-card> — never triggers the
      // theme's handler, so it must not be reported either. Inside the card, apply the theme's
      // exact predicate: it navigates unless the click is on an interactive control other than the
      // main link. This excludes the single-variant quick-add button (its add is reported by the
      // cart controller) and the gallery/slideshow arrows, which are not shopper navigation at all.
      // The multi-variant quick-add button was already handled above as a quick view.
      const productCard = event.target.closest('product-card');
      if (!productCard || !card.contains(productCard)) return;
      const interactive = event.target.closest('button, input, label, select, a, [tabindex="1"]');
      const mainLink = event.target.closest('a[ref="productCardLink"]');
      if (interactive && interactive !== mainLink) return;

      fsEvent(SURFACE.productClickEvent, {
        ...attribution(),
        productID: card.dataset.productId,
        position: parseInt(card.dataset.fsPosition, 10) || 1,
      });
  };

  /* ------------------------------------------------------------- swatch switch */

  /**
   * Colours on this store are separate products linked through the style_family
   * metafield, so tapping a swatch on a card moves that tile to a different product
   * id. Fast Simon needs that link — without it the colour that earned the click
   * gets no credit for what happens next. The tile's original product stays the
   * source for every switch made on that tile.
   */
  const originalProductByCard = new WeakMap();

  const onGridSwatchClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const swatch = event.target.closest('a[data-mapped-product-url]');
      if (!swatch) return;

      const card = swatch.closest('.product-grid__item[data-product-id]');
      if (!card) return;

      const sourceProductID = card.dataset.productId;
      if (!originalProductByCard.has(card)) originalProductByCard.set(card, sourceProductID);
      const originalProductID = originalProductByCard.get(card);

      const targetProductID =
        swatch.dataset.mappedProductId || fsIdByHandle.get(handleFromUrl(swatch.href)) || '';
      // Size picks and a colour already credited are not switches.
      if (!targetProductID || targetProductID === originalProductID) return;

      fsEvent(['ProductSwatchSwitchPerformed', 'SwatchChanged'], {
        productID: targetProductID,
        sourceProductID: originalProductID,
        position: parseInt(card.dataset.fsPosition, 10) || undefined,
      });
  };

  // Optional "search within results" input, if the merchant adds one. `let` + re-acquired by
  // reinitialize(): the input lives inside the re-rendered section.
  let withinInput = document.getElementById('search-within-results-bar');
  const onWithinInput = (event) => {
    const value = event.target.value;

    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      state.searchWithin = value;
      rerunFromFilters();
    }, DEBOUNCE_MS);
  };

  const onPopState = () => {
    // Back/forward restores a URL that may differ from the in-flight edits. Cancel any pending
    // debounced edit (search-within, price slider) FIRST and reset transient slider state, so a
    // timer that was scheduled before the navigation cannot fire afterward and overwrite the
    // freshly-restored URL state with the value the shopper was mid-typing/dragging.
    window.clearTimeout(debounceTimer);
    debounceTimer = null;
    window.clearTimeout(priceCommitTimer);
    priceCommitTimer = null;
    priceDragging = false;
    pendingFacets = null;

    readUrl();
    renderSort();
    // The search-within input still shows whatever the shopper last typed; reflect the restored
    // state so the field matches the URL rather than the abandoned edit.
    if (withinInput) withinInput.value = state.searchWithin;
    run();
  };

  /* ---------------------------------------------------------------------- boot */

  // Capture the collection page's own product-card design from the native cards before Fast Simon
  // replaces them, so every fetched card can be re-dressed to match (see the transplant above).
  // On /search this stays null and the fetched cards keep their own (correct) design untouched.
  if (isCollection) cardDesign = captureCardDesign(grid);

  /* ---------------------------------------------------- section re-render (editor) */

  /**
   * Registry of every listener that must survive a section re-render: bindDomListeners()
   * registers through listen(), unbindDomListeners() removes exactly what was added. The
   * pairing is what makes reinitialize() safe to call repeatedly without stacking handlers —
   * the failure mode of simply re-running this controller instead.
   */
  const domListeners = [];
  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    domListeners.push([target, type, handler, options]);
  };
  const unbindDomListeners = () => {
    for (const [target, type, handler, options] of domListeners.splice(0)) {
      target.removeEventListener(type, handler, options);
    }
  };

  const bindDomListeners = () => {
    listen(document, 'change', onChange);
    // Price slider: live feedback while dragging; no request is sent from these three.
    listen(document, 'input', onSliderInput);
    listen(document, 'pointerdown', onSliderPointerDown, { passive: true });
    listen(window, 'pointerup', endPriceDrag, { passive: true });
    listen(window, 'pointercancel', endPriceDrag, { passive: true });
    listen(document, 'click', onChipClick); // chip removal / clear all
    listen(grid, 'click', onGridNavClick, { passive: true }); // product / tile / quick-view
    listen(grid, 'click', onGridSwatchClick, { passive: true }); // swatch switches
    if (withinInput) listen(withinInput, 'input', onWithinInput);
    listen(window, 'popstate', onPopState);
  };

  /**
   * Full teardown + rebind after the theme editor re-renders the results section — either
   * surface — (shopify:section:load). The re-render REPLACES the section's nodes — grid,
   * facet chrome, search-within input — so every detached reference the controller holds
   * (grid, gridWrapper, the $$ container arrays, withinInput, the grid-bound click listeners)
   * must be dropped and re-acquired, and anything that could still write to the old DOM must
   * be cancelled: an abort alone would not stop a settled SDK callback from rendering into
   * dead nodes. Settings are re-derived from the fresh config element (readConfig), so a
   * mid-session settings edit takes effect on the next takeover, not after a page reload.
   */
  const reinitialize = () => {
    // 1. Stop everything that could still touch the old DOM. The seq bump invalidates late SDK
    //    callbacks (onProductsLoaded/onFacetsLoaded) exactly like failRequest does.
    if (cardsAbort) cardsAbort.abort();
    cardsAbort = null;
    ++requestSeq;
    window.clearTimeout(requestTimer);
    requestTimer = null;
    window.clearTimeout(debounceTimer);
    debounceTimer = null;
    window.clearTimeout(priceCommitTimer);
    priceCommitTimer = null;
    priceDragging = false;
    pendingFacets = null;
    // A pending pre-readiness run describes the OLD surface — readiness must not flush it
    // against whatever this re-render left behind (it would fire the SDK method and report a
    // listing for a surface that may no longer exist). A successful re-init below re-queues
    // through run(); a stand-down (readConfig failure) flushes nothing.
    pendingRun = null;

    unbindDomListeners();

    // 2. Re-derive the settings from the FRESH config element (a settings edit changes what it
    //    carries — products per page, sort options, labels — and stale constants would keep
    //    the old values alive) and re-acquire the fresh nodes. The section id is unchanged
    //    across a re-render, so the queries land on this section's new nodes; the old ones are
    //    detached and GC'd along with whatever was still bound to them.
    if (!readConfig()) return; // the section no longer renders a usable Fast Simon surface
    gridWrapper = grid.parentElement;
    groupContainers = $$('[data-fs-facet-groups]');
    chipContainers = $$('[data-fs-chips]');
    countEls = $$('[data-fs-count]');
    sortWrappers = $$('[data-fs-sort]');
    sortOptionContainers = $$('[data-fs-sort-options]');
    sortCurrentEls = $$('[data-fs-sort-current]');
    filterCountEls = $$('[data-fs-filter-count]');
    seeResultsEls = $$('[data-fs-see-results]');
    withinInput = document.getElementById('search-within-results-bar');

    // Card markup fetched BEFORE the edit must not be served into the grid AFTER it — cache
    // entries live up to 30s and would carry the pre-edit card structure.
    cardCache.clear();

    setBusy(false); // clear any loading dim from the fresh markup before the new takeover

    // 3. The fresh server render brings the section's native cards back — recapture the design
    //    they carry (keeping the previous capture when there is nothing new to learn), then
    //    rebind and take over again.
    if (isCollection) {
      const fresh = captureCardDesign(grid);
      if (fresh) cardDesign = fresh;
    }
    bindDomListeners();
    renderSort();
    run(); // post-readiness in the editor, so this executes immediately
  };

  // When the merchant edits a setting (or the "product card" block) the theme editor
  // re-renders the whole section and fires this event. The controller re-initializes against
  // the fresh DOM instead of continuing against detached originals — see reinitialize().
  // BOTH surfaces qualify: each renders its config element and grid inside its own section,
  // tagged `data-grid-section-id`, so the id guard below covers /search as well as the PLP.
  // This listener is deliberately NOT in the bind/unbind registry: it is section-scoped by
  // id, not node-scoped.
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
    // Only react to a re-render of the results section itself, not any other section.
    if (!section || section.id !== `shopify-section-${gridSectionId}`) return;
    reinitialize();
  });

  bindDomListeners();

  readUrl();
  // Seed the attribution snapshot from the URL so the server-rendered cards visible before the
  // first Fast Simon response are attributed to the listing the shopper actually landed on.
  commitAttribution();
  renderSort();
  writeUrl(true);
  run();
})();

